import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { theme } from "../../lib/theme";
import type { TxInsightsResponse } from "../../lib/api";
import {
  privacyBreakdown,
  setLabel,
  SMALL_SET_MAX,
  MAX_SELECTED_SETS,
  type PrivacyTier,
} from "../../lib/view-insights";
import { Badge, ChartCard, ChartTitle, Table, TableWrapper, Button } from "../ui";

/**
 * Where a saved view's transactions hide: how many sit in a real crowd, a thin
 * one, or none at all under the latest clustering run — and which privacy sets
 * the view keeps landing in. A cluster the view covers most of is effectively
 * this view's fingerprint.
 *
 * Rows are selectable: a selected set is stacked, in its own colour, inside
 * the page's histograms and fee scatter, so "where does this set sit?" reads
 * across every chart at once.
 */

const Subtitle = styled.p`
  margin: 0 0 ${theme.spacing.md};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const Headline = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

const HeadlineStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: ${theme.fontSize.lg};
    font-weight: 700;
    color: ${theme.colors.text};
  }

  span {
    font-size: ${theme.fontSize.xs};
    color: ${theme.colors.textMuted};
  }
`;

/** Segments sit 2px apart so neighbouring tiers never bleed into one another. */
const StackedBar = styled.div`
  display: flex;
  gap: 2px;
  height: 14px;
  margin-bottom: ${theme.spacing.sm};
`;

const Segment = styled.div<{ tone: string; grow: number }>`
  flex: ${(p) => p.grow} 0 0;
  min-width: 3px;
  background: ${(p) => p.tone};
  border-radius: 2px;
`;

const Legend = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: ${theme.spacing.xs} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  b {
    color: ${theme.colors.text};
    font-family: monospace;
    font-weight: 400;
  }
`;

const Swatch = styled.span<{ tone: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${(p) => p.tone};
  flex-shrink: 0;
`;

const ShareBar = styled.div`
  position: relative;
  flex: none;
  width: 100px;
  height: 6px;
  background: ${theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
`;

const ShareFill = styled.div<{ pct: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(p) => p.pct}%;
  background: ${theme.colors.primary};
  border-radius: 3px;
`;

const SelectHint = styled.p`
  margin: 0 0 ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const SetRow = styled.tr<{ selectable: boolean; selected: boolean }>`
  cursor: ${(p) => (p.selectable ? "pointer" : "default")};
  background: ${(p) => (p.selected ? theme.colors.bgHover : "transparent")};
`;

/** The selection's colour, doubling as the legend for every chart that uses it. */
const SelectSwatch = styled.span<{ tone?: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: ${theme.spacing.sm};
  border-radius: 2px;
  vertical-align: middle;
  background: ${(p) => p.tone ?? "transparent"};
  border: 1px solid ${(p) => p.tone ?? theme.colors.border};
`;

const OpenLink = styled(Link)`
  color: ${theme.colors.textMuted};
  text-decoration: none;
  padding: 0 ${theme.spacing.xs};

  &:hover {
    color: ${theme.colors.primary};
  }
`;

/**
 * Fixed-height scroll area for the sets table: "show all" can mean hundreds of
 * rows, and the fee chart beside this card grows to match its height.
 */
const ScrollTable = styled(TableWrapper)`
  max-height: 440px;
  overflow-y: auto;
  overscroll-behavior-y: contain;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${theme.colors.bgCard};
  }
`;

const Empty = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};
  margin: 0;
`;

const TIER_META: Record<PrivacyTier, { label: string; tone: string }> = {
  outlier: { label: "Outlier (set of 1)", tone: theme.colors.danger },
  small: { label: `Small set (2–${SMALL_SET_MAX})`, tone: theme.colors.warning },
  set: { label: `Set of ${SMALL_SET_MAX + 1}+`, tone: theme.colors.success },
  unanalyzed: { label: "Not yet analyzed", tone: theme.colors.border },
};
const TIER_ORDER: PrivacyTier[] = ["set", "small", "outlier", "unanalyzed"];

const TOP_CLUSTERS = 10;

const pctText = (n: number, of: number) => {
  if (of === 0) return "0%";
  const p = (n / of) * 100;
  return p > 0 && p < 0.1 ? "<0.1%" : `${p.toFixed(1)}%`;
};

export interface PrivacySetsCardProps {
  networkId: string;
  insights: TxInsightsResponse | undefined;
  /** Selected cluster ids (-1 = outliers), in selection order. */
  selected: number[];
  onToggle: (clusterId: number) => void;
  /** Colour of a selected set; undefined when it isn't selected. */
  colorOf: (clusterId: number) => string | undefined;
}

export function PrivacySetsCard({
  networkId,
  insights,
  selected,
  onToggle,
  colorOf,
}: PrivacySetsCardProps) {
  const [showAll, setShowAll] = useState(false);
  const breakdown = useMemo(() => (insights ? privacyBreakdown(insights) : null), [insights]);

  if (!insights || !breakdown) {
    return (
      <ChartCard>
        <ChartTitle>Privacy sets</ChartTitle>
        <Empty>Loading…</Empty>
      </ChartCard>
    );
  }

  const { matching, run } = insights;
  const clusters = insights.clusters.filter((c) => c.clusterId !== -1);
  const outliers = insights.clusters.find((c) => c.clusterId === -1);
  // Outliers lead, pinned like on the Privacy Sets page: where the unique txs
  // land is usually the first thing worth highlighting.
  const shown = [
    ...(outliers ? [outliers] : []),
    ...(showAll ? clusters : clusters.slice(0, TOP_CLUSTERS)),
  ];
  const full = selected.length >= MAX_SELECTED_SETS;

  return (
    <ChartCard>
      <ChartTitle>Privacy sets</ChartTitle>
      <Subtitle>
        {run
          ? `Where the matching txs sit in the latest clustering run (${new Date(run.computedAt).toLocaleString()}).`
          : "No clustering run yet. Analysis runs automatically every 10 minutes."}
      </Subtitle>

      {matching === 0 ? (
        <Empty>No transactions match this view.</Empty>
      ) : (
        <>
          <Headline>
            <HeadlineStat>
              <strong>{breakdown.medianSetSize?.toLocaleString() ?? "–"}</strong>
              <span>typical set size</span>
            </HeadlineStat>
            <HeadlineStat>
              <strong>{breakdown.setsTouched.toLocaleString()}</strong>
              <span>privacy sets touched</span>
            </HeadlineStat>
            <HeadlineStat>
              <strong
                style={{ color: breakdown.tiers.outlier > 0 ? theme.colors.danger : undefined }}
              >
                {pctText(breakdown.tiers.outlier, insights.analyzed)}
              </strong>
              <span>of analyzed are outliers</span>
            </HeadlineStat>
          </Headline>

          <StackedBar role="img" aria-label="Matching transactions by privacy set size">
            {TIER_ORDER.filter((t) => breakdown.tiers[t] > 0).map((t) => (
              <Segment
                key={t}
                tone={TIER_META[t].tone}
                grow={breakdown.tiers[t]}
                title={`${TIER_META[t].label}: ${breakdown.tiers[t].toLocaleString()} (${pctText(breakdown.tiers[t], matching)})`}
              />
            ))}
          </StackedBar>
          <Legend>
            {TIER_ORDER.map((t) => (
              <LegendItem key={t}>
                <Swatch tone={TIER_META[t].tone} />
                {TIER_META[t].label}
                <b>{breakdown.tiers[t].toLocaleString()}</b>
                <span>{pctText(breakdown.tiers[t], matching)}</span>
              </LegendItem>
            ))}
          </Legend>

          {shown.length > 0 && (
            <>
              <SelectHint>
                {full
                  ? `${MAX_SELECTED_SETS} sets highlighted, the most the charts keep apart. Deselect one to pick another.`
                  : `Click sets to highlight them in the fee spread and distributions (up to ${MAX_SELECTED_SETS}).`}
              </SelectHint>
              <ScrollTable>
                <Table>
                  <thead>
                    <tr>
                      <th>Cluster</th>
                      <th>In view</th>
                      <th>Set size</th>
                      <th>Share of set in view</th>
                      <th aria-label="Open set" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((c) => {
                      const isOutlier = c.clusterId === -1;
                      const tone = colorOf(c.clusterId);
                      const selectable = tone !== undefined || !full;
                      return (
                        <SetRow
                          key={c.clusterId}
                          selectable={selectable}
                          selected={tone !== undefined}
                          aria-selected={tone !== undefined}
                          onClick={() => selectable && onToggle(c.clusterId)}
                          title={
                            tone !== undefined
                              ? "Stop highlighting"
                              : selectable
                                ? "Highlight in the charts"
                                : undefined
                          }
                        >
                          <td style={{ whiteSpace: "nowrap" }}>
                            <SelectSwatch tone={tone} />
                            <Badge
                              color={
                                isOutlier
                                  ? theme.colors.outlier
                                  : theme.colors.cluster[c.clusterId % theme.colors.cluster.length]
                              }
                            >
                              {setLabel(c.clusterId)}
                            </Badge>
                          </td>
                          <td style={{ fontFamily: "monospace" }}>{c.matching.toLocaleString()}</td>
                          <td>
                            <Badge
                              color={
                                isOutlier
                                  ? theme.colors.danger
                                  : c.size <= SMALL_SET_MAX
                                    ? theme.colors.warning
                                    : theme.colors.success
                              }
                            >
                              {isOutlier ? "1 each" : c.size.toLocaleString()}
                            </Badge>
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: theme.spacing.sm,
                              }}
                            >
                              <ShareBar>
                                <ShareFill
                                  pct={c.size > 0 ? Math.min(100, (c.matching / c.size) * 100) : 0}
                                />
                              </ShareBar>
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: theme.fontSize.xs,
                                  minWidth: 44,
                                  textAlign: "right",
                                }}
                              >
                                {pctText(c.matching, c.size)}
                              </span>
                            </div>
                          </td>
                          <td>
                            <OpenLink
                              to={`/${networkId}/privacy-sets?cluster=${c.clusterId}`}
                              onClick={(e) => e.stopPropagation()}
                              title="Open this privacy set"
                            >
                              ↗
                            </OpenLink>
                          </td>
                        </SetRow>
                      );
                    })}
                  </tbody>
                </Table>
              </ScrollTable>
              {clusters.length > TOP_CLUSTERS && (
                <Button
                  variant="ghost"
                  style={{ marginTop: theme.spacing.sm }}
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? "Show top 10" : `Show all ${clusters.length} sets`}
                </Button>
              )}
            </>
          )}
        </>
      )}
    </ChartCard>
  );
}
